import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';

import {
  Button,
  Checkbox,
  Combobox,
  Tooltip
} from '@salesforce/design-system-react';

import './SpotlightConfiguration.scss';

class SpotlightConfiguration extends Component {
  constructor(props) {
    super(props);
    this.getI18nID = suffix =>
      `components.ecs.blocks.configuration.spotlight.${suffix}`;
    this.toOption = this.toOption.bind(this);

    const spotlightEnabled = !!this.props.spotlight?.length;
    this.state = {
      spotlightEnabled,
      spotlightValuesIsOpen: {}
    };
  }
  handleSpotlightAttributeSelect(idx) {
    return (event, { selection }) => {
      const attribute = selection?.[0]?.id;
      let spotlight = [...this.props.spotlight];
      if (idx === 0 && !spotlight.length) {
        spotlight = [{}];
      }
      if (attribute === 'none') {
        spotlight[idx] = { key: null, value: null };
      } else {
        spotlight[idx] = { key: attribute, value: null };
      }

      this.props.onSpotlightChange(spotlight);
    };
  }

  handleSpotlightRemove(idx) {
    const spotlight = [...this.props.spotlight];
    spotlight.splice(idx, 1);
    this.props.onSpotlightChange(spotlight);
  }

  handleSpotlightValueSelect(idx) {
    return (event, { selection }) => {
      const spotlight = [...this.props.spotlight];
      let value = (selection || [])
        .map(s => s.id)
        .sort((a, b) => a.localeCompare(b));
      if (value.length === 0) {
        value = null;
      }
      spotlight[idx] = { ...spotlight[idx], value };
      this.props.onSpotlightChange(spotlight);
    };
  }

  toOption(key) {
    const { formatMessage } = this.props.intl;
    return {
      id: key,
      value: key,
      label:
        key === 'none'
          ? formatMessage({
              id: 'components.ecs.blocks.configuration.spotlight.none'
            })
          : key
    };
  }

  spotlightAttributeOptions(idx) {
    if (this.props.attributes) {
      return (
        this.props.attributes
          // filter out attributes that are in other spotlight boxes
          .filter(attr => {
            const index = this.props.spotlight.findIndex(
              spot => spot.key === attr
            );
            return index < 0 || index === idx;
          })
          .map(this.toOption)
      );
    }
    return [];
  }

  spotlightValueOptions(idx) {
    const attribute = this.selectedSpotlightAttribute(idx)?.[0]?.id;
    if (attribute && this.props.attributeValues?.[attribute]) {
      const values = ['none', ...this.props.attributeValues[attribute]];
      return values.map(this.toOption);
    }
    return [];
  }

  selectedSpotlightAttribute(idx) {
    const attribute = this.props.spotlight[idx]?.key;
    if (attribute) {
      return [this.toOption(attribute)];
    }
    return [];
  }

  selectedSpotlightValue(idx) {
    const value = this.props.spotlight[idx]?.value;
    if (value) {
      if (Array.isArray(value)) {
        return value.map(this.toOption);
      } else {
        return [this.toOption(value)];
      }
    }
    return [];
  }

  renderSpotlight(spotlightPart, idx) {
    const { formatMessage } = this.props.intl;
    const spotlights = this.props.spotlight || [];

    const labels = {
      key: formatMessage({ id: this.getI18nID('key') }),
      value: formatMessage({ id: this.getI18nID('value') }),
      keyPlaceholder: formatMessage({ id: this.getI18nID('keyPlaceholder') }),
      valuePlaceholder: formatMessage({
        id: this.getI18nID('valuePlaceholder')
      }),
      multipleValuesSelected: formatMessage(
        { id: this.getI18nID('multipleValuesSelected') },
        { count: this.selectedSpotlightValue(idx).length }
      )
    };

    const valueDisabled =
      !this.state.spotlightEnabled ||
      !this.selectedSpotlightAttribute(idx).length;

    const valueCombobox = !valueDisabled ? (
      <Combobox
        variant="readonly"
        multiple
        selection={this.selectedSpotlightValue(idx)}
        options={this.spotlightValueOptions(idx)}
        isOpen={this.state.spotlightValuesIsOpen[idx]}
        events={{
          onSelect: this.handleSpotlightValueSelect(idx),
          onRequestOpen: _ => {
            const spotlightValuesIsOpen = {
              ...this.state.spotlightValuesIsOpen
            };
            spotlightValuesIsOpen[idx] = true;
            this.setState({ spotlightValuesIsOpen });
          },
          onBlur: _ => {
            const spotlightValuesIsOpen = {
              ...this.state.spotlightValuesIsOpen
            };
            spotlightValuesIsOpen[idx] = false;
            this.setState({ spotlightValuesIsOpen });
          },
          onRequestRemoveSelectedOption: this.handleSpotlightValueSelect(idx)
        }}
        labels={{
          label: labels.value,
          placeholderReadOnly: labels.valuePlaceholder,
          multipleOptionsSelected: labels.multipleValuesSelected
        }}
      />
    ) : null;

    const deleteButton = idx => (
      <Button
        className="select-again-delete-rule-button"
        iconCategory="utility"
        iconName="delete"
        iconVariant="border-filled"
        variant="icon"
        onClick={() => this.handleSpotlightRemove(idx)}
      />
    );

    return (
      <li key={`spotlight-row-${idx}`} className="spotlight-row">
        <div className="slds-col attribute-cell deletable">
          <Combobox
            variant="readonly"
            selection={this.selectedSpotlightAttribute(idx)}
            options={this.spotlightAttributeOptions(idx)}
            events={{ onSelect: this.handleSpotlightAttributeSelect(idx) }}
            singleInputDisabled={!this.state.spotlightEnabled}
            labels={{
              label: labels.key,
              placeholderReadOnly: labels.keyPlaceholder
            }}
          />
          {spotlights.length > 1 ? deleteButton(idx) : null}
        </div>
        <div className="slds-col attribute-cell">{valueCombobox}</div>
      </li>
    );
  }

  addAnother() {
    const spotlight = this.props.spotlight || [];
    this.props.onSpotlightChange([...spotlight, {}]);
  }

  renderAddAnother() {
    const spotlights = this.props.spotlight || [];
    if (spotlights.length && spotlights.every(spot => !!spot.value)) {
      return (
        <span className="add-another" onClick={() => this.addAnother()}>
          <FormattedMessage id={this.getI18nID('addAnother')} />
        </span>
      );
    }
  }

  render() {
    const { formatMessage } = this.props.intl;

    const labels = {
      toggleOff: formatMessage({ id: this.getI18nID('toggle_off') }),
      toggleOn: formatMessage({ id: this.getI18nID('toggle_on') }),
      tooltip: formatMessage({ id: this.getI18nID('tooltip') })
    };
    let spotlightRows = [];
    if (this.props.spotlight?.length) {
      spotlightRows = this.props.spotlight.map((spotlight, idx) =>
        this.renderSpotlight(spotlight, idx)
      );
    } else {
      spotlightRows = [this.renderSpotlight({}, 0)];
    }

    return (
      <React.Fragment>
        <div className="spotlight-info">
          <div className="spotlight-header">
            <div className="spotlight-details">
              <FormattedMessage id="components.ecs.blocks.configuration.spotlight.details" />
              <Tooltip
                className="spotlight-tooltip"
                align="right"
                content={labels.tooltip}
                variant="learnMore"
              />
            </div>
            <Checkbox
              className="spotlight-toggle"
              id="spotlight-toggle"
              variant="toggle"
              labels={{
                toggleDisabled: labels.toggleOff,
                toggleEnabled: labels.toggleOn
              }}
              checked={this.state.spotlightEnabled}
              onChange={(event, { checked }) => {
                this.setState({ spotlightEnabled: checked }, () => {
                  if (!this.state.spotlightEnabled) {
                    this.props.onSpotlightChange([]);
                  }
                });
              }}
            />
          </div>
        </div>
        <ul>{spotlightRows}</ul>
        {this.renderAddAnother()}
      </React.Fragment>
    );
  }
}

SpotlightConfiguration.propTypes = {
  spotlight: PropTypes.array,
  attributes: PropTypes.arrayOf(PropTypes.string),
  attributeValues: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  onSpotlightChange: PropTypes.func,
  intl: intlShape.isRequired
};

export default injectIntl(SpotlightConfiguration);
